import React, { useState, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckSquare,
  faSearch,
  faSortAlphaDown,
  faSortAlphaUp,
  faInfoCircle,
  faTrash,
  faSitemap,
  faPaste,
  faCheckCircle,
  faTimesCircle,
  faPlus,
  faTimes,
  faUpRightAndDownLeftFromCenter
} from '@fortawesome/free-solid-svg-icons';

const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS'];

// Validate that a string is a well-formed http(s) URL
export const isValidHttpUrl = (value) => {
  if (typeof value !== 'string') return false;
  try {
    const u = new URL(value.trim());
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
};

// Parse pasted text into valid / invalid entries.
// Each line may be "https://..." or "POST https://..." (leading method optional).
export const parsePastedUrls = (text, defaultMethod = 'GET') => {
  const lines = (text || '')
    .split(/[\r\n]+/)
    .map(l => l.trim())
    .filter(Boolean);

  const valid = [];
  const invalidLines = [];
  const seen = new Set();

  for (const line of lines) {
    let method = defaultMethod;
    let urlPart = line;

    // Optional leading HTTP method token: "POST https://..."
    const match = line.match(/^([A-Za-z]+)\s+(\S.*)$/);
    if (match && HTTP_METHODS.includes(match[1].toUpperCase())) {
      method = match[1].toUpperCase();
      urlPart = match[2].trim();
    }

    if (isValidHttpUrl(urlPart)) {
      const key = `${method} ${urlPart}`;
      if (!seen.has(key)) {
        seen.add(key);
        valid.push({ method, url: urlPart });
      }
    } else {
      invalidLines.push(line);
    }
  }

  return { valid, invalidLines };
};

/**
 * AuthSmashUrlSelector - Manual URL selection with checkboxes
 *
 * The visible list (`endpoints`) is the stable set of URLs known to the project
 * (site map + persisted project URLs). Checking/unchecking only toggles which of
 * them are part of the test (`selectedEndpoints`) — unchecking NEVER removes a row.
 * To permanently remove a project URL, use its trash icon (calls onDeleteUrl).
 */
const AuthSmashUrlSelector = ({
  endpoints = [],
  selectedEndpoints = [],
  onSelectionChange,
  onAddUrls,       // (Array<{method,url}>) => void  — persist pasted/added URLs to the project
  onDeleteUrl,     // ({method,url}) => void          — permanently remove a project URL
  onDeleteUrls,    // (Array<{method,url}>) => void   — permanently remove several project URLs
  onCopyToSiteTree,// (Array<{method,url}>) => void   — add checked URLs to the site tree (attributed)
  isDarkMode = false
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterMethod, setFilterMethod] = useState('all');
  const [filterHost, setFilterHost] = useState('all');
  const [sortBy, setSortBy] = useState('url'); // 'url' or 'method'
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'

  // Paste panel state
  const [showPaste, setShowPaste] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [pasteMethod, setPasteMethod] = useState('GET');

  // Expanded (large modal) view for browsing many URLs
  const [isExpanded, setIsExpanded] = useState(false);

  // Transform siteMap/manual data into {method, url, host, source} entries
  const transformedEndpoints = useMemo(() => {
    if (!endpoints || !Array.isArray(endpoints)) return [];
    const result = [];
    for (const entry of endpoints) {
      // SiteMap format {host, path, methods[]}: expand each method
      if (entry.methods && Array.isArray(entry.methods)) {
        for (const method of entry.methods) {
          const url = `https://${entry.host}${entry.path || '/'}`;
          result.push({ method, url, host: entry.host, path: entry.path, source: entry.source || 'sitemap' });
        }
      } else if (entry.method && entry.url) {
        // Direct {method, url} format
        const host = entry.url.match(/https?:\/\/([^/]+)/)?.[1] || 'unknown';
        result.push({ ...entry, host, source: entry.source || 'manual' });
      }
    }
    return result;
  }, [endpoints]);

  const availableMethods = useMemo(() => {
    const methods = [...new Set(transformedEndpoints.map(ep => ep?.method).filter(Boolean))];
    return methods.sort();
  }, [transformedEndpoints]);

  const availableHosts = useMemo(() => {
    const hosts = [...new Set(transformedEndpoints.map(ep => ep?.host).filter(Boolean))];
    return hosts.sort();
  }, [transformedEndpoints]);

  const filteredEndpoints = useMemo(() => {
    let filtered = [...transformedEndpoints];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(ep =>
        ep.url.toLowerCase().includes(term) ||
        ep.method.toLowerCase().includes(term) ||
        ep.host?.toLowerCase().includes(term)
      );
    }
    if (filterMethod !== 'all') {
      filtered = filtered.filter(ep => ep.method === filterMethod);
    }
    if (filterHost !== 'all') {
      filtered = filtered.filter(ep => ep.host === filterHost);
    }

    filtered.sort((a, b) => {
      const compareA = sortBy === 'url' ? (a?.url || '') : (a?.method || '');
      const compareB = sortBy === 'url' ? (b?.url || '') : (b?.method || '');
      return sortOrder === 'asc' ? compareA.localeCompare(compareB) : compareB.localeCompare(compareA);
    });

    return filtered;
  }, [transformedEndpoints, searchTerm, filterMethod, filterHost, sortBy, sortOrder]);

  const isSelected = (endpoint) =>
    selectedEndpoints.some(sel => sel.method === endpoint.method && sel.url === endpoint.url);

  const toggleEndpoint = (endpoint) => {
    if (!endpoint || !onSelectionChange) return;
    if (isSelected(endpoint)) {
      onSelectionChange(
        selectedEndpoints.filter(sel => !(sel.method === endpoint.method && sel.url === endpoint.url))
      );
    } else {
      onSelectionChange([...selectedEndpoints, { method: endpoint.method, url: endpoint.url }]);
    }
  };

  const selectAll = () => {
    const newSelections = [...selectedEndpoints];
    filteredEndpoints.forEach(ep => {
      if (!isSelected(ep)) newSelections.push({ method: ep.method, url: ep.url });
    });
    onSelectionChange(newSelections);
  };

  const deselectAll = () => {
    const newSelections = selectedEndpoints.filter(sel =>
      !filteredEndpoints.some(ep => ep.method === sel.method && ep.url === sel.url)
    );
    onSelectionChange(newSelections);
  };

  const toggleSortOrder = () => setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));

  const handleDelete = (endpoint, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!onDeleteUrl) return;
    onDeleteUrl({ method: endpoint.method, url: endpoint.url });
  };

  // Live paste validation
  const pasteResult = useMemo(
    () => parsePastedUrls(pasteText, pasteMethod),
    [pasteText, pasteMethod]
  );

  const handleAddPasted = () => {
    if (pasteResult.valid.length === 0 || !onAddUrls) return;
    onAddUrls(pasteResult.valid.map(v => ({ ...v, source: 'paste' })));
    setPasteText('');
    setShowPaste(false);
  };

  const selectionStats = useMemo(() => {
    const visibleSelected = filteredEndpoints.filter(ep => isSelected(ep)).length;
    return {
      total: selectedEndpoints.length,
      visible: visibleSelected,
      filtered: filteredEndpoints.length
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEndpoints, filteredEndpoints]);

  // Any selected URL can be removed from the list (site-map entries get excluded)
  const deletableSelected = useMemo(() => {
    return transformedEndpoints.filter(ep => isSelected(ep));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transformedEndpoints, selectedEndpoints]);

  const handleDeleteSelected = () => {
    if (!onDeleteUrls || deletableSelected.length === 0) return;
    if (!window.confirm(
      `Permanently remove ${deletableSelected.length} selected URL${deletableSelected.length !== 1 ? 's' : ''} from the project? This cannot be undone.`
    )) return;
    onDeleteUrls(deletableSelected.map(ep => ({ method: ep.method, url: ep.url })));
  };

  const handleCopyToSiteTree = () => {
    if (!onCopyToSiteTree || deletableSelected.length === 0) return;
    onCopyToSiteTree(deletableSelected.map(ep => ({ method: ep.method, url: ep.url })));
  };

  // Search + filter + bulk-action controls (reused inline and in the expanded modal)
  const renderControls = () => (
    <div className="space-y-1.5">
      <div className="relative">
        <FontAwesomeIcon icon={faSearch} className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-500 text-xs" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search URLs, methods, or hosts..."
          className={`w-full pl-7 pr-2 py-1.5 border rounded text-xs ${
            isDarkMode ? 'bg-gray-900 border-gray-600 text-gray-300 placeholder-gray-600' : 'bg-white border-gray-300 text-gray-700 placeholder-gray-400'
          }`}
        />
      </div>

      <div className="flex gap-1.5 items-center flex-wrap">
        <select
          value={filterHost}
          onChange={(e) => setFilterHost(e.target.value)}
          className={`flex-1 px-2 py-1 border rounded text-xs ${isDarkMode ? 'bg-gray-900 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700'}`}
        >
          <option value="all">All Hosts ({availableHosts.length})</option>
          {availableHosts.map(host => <option key={host} value={host}>{host}</option>)}
        </select>

        <select
          value={filterMethod}
          onChange={(e) => setFilterMethod(e.target.value)}
          className={`px-2 py-1 border rounded text-xs ${isDarkMode ? 'bg-gray-900 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700'}`}
        >
          <option value="all">All Methods</option>
          {availableMethods.map(method => <option key={method} value={method}>{method}</option>)}
        </select>

        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className={`px-2 py-1 border rounded text-xs ${isDarkMode ? 'bg-gray-900 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700'}`}
        >
          <option value="url">Sort by URL</option>
          <option value="method">Sort by Method</option>
        </select>

        <button
          onClick={toggleSortOrder}
          className={`px-2 py-1 border rounded ${isDarkMode ? 'border-gray-600 hover:bg-gray-800' : 'border-gray-300 hover:bg-gray-50'}`}
          title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
        >
          <FontAwesomeIcon icon={sortOrder === 'asc' ? faSortAlphaDown : faSortAlphaUp} className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} />
        </button>
      </div>

      {/* Bulk Actions */}
      <div className="flex gap-1.5">
        <button onClick={selectAll} className="flex-1 px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-xs font-medium">
          Select All Visible
        </button>
        <button
          onClick={deselectAll}
          className={`flex-1 px-2 py-1 rounded text-xs font-medium ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-300 text-gray-700 hover:bg-gray-400'}`}
        >
          Deselect All Visible
        </button>
      </div>

      {/* Copy checked URLs into the site tree (attributed to the baseline profile) */}
      {onCopyToSiteTree && deletableSelected.length > 0 && (
        <button
          onClick={handleCopyToSiteTree}
          className={`w-full px-2 py-1 rounded text-xs font-medium flex items-center justify-center gap-1.5 border transition-colors ${
            isDarkMode
              ? 'bg-teal-900/30 text-teal-300 border-teal-800 hover:bg-teal-900/50'
              : 'bg-teal-50 text-teal-600 border-teal-200 hover:bg-teal-100'
          }`}
          title="Add the checked URLs to the site tree, attributed to the baseline (admin) profile"
        >
          <FontAwesomeIcon icon={faSitemap} className="text-[10px]" />
          Copy to Site Tree ({deletableSelected.length})
        </button>
      )}

      {/* Delete selected project URLs */}
      {onDeleteUrls && deletableSelected.length > 0 && (
        <button
          onClick={handleDeleteSelected}
          className={`w-full px-2 py-1 rounded text-xs font-medium flex items-center justify-center gap-1.5 border transition-colors ${
            isDarkMode
              ? 'bg-red-900/30 text-red-300 border-red-800 hover:bg-red-900/50'
              : 'bg-red-50 text-red-600 border-red-200 hover:bg-red-100'
          }`}
          title="Permanently remove the checked project URLs from the list"
        >
          <FontAwesomeIcon icon={faTrash} className="text-[10px]" />
          Delete Selected ({deletableSelected.length})
        </button>
      )}
    </div>
  );

  // The scrollable endpoint list. heightClass controls how tall it is
  // (compact inline vs tall in the expanded modal).
  const renderList = (heightClass) => (
    <div className={`border rounded ${heightClass} overflow-y-auto ${isDarkMode ? 'border-gray-700' : 'border-gray-300'}`}>
      {filteredEndpoints.length === 0 ? (
        <div className={`p-4 text-center ${isDarkMode ? 'text-gray-600' : 'text-gray-500'}`}>
          <FontAwesomeIcon icon={faInfoCircle} className="text-xl mb-1" />
          <p className="text-xs">
            {transformedEndpoints.length === 0
              ? 'No URLs yet. Use "Paste URLs" above to add some.'
              : 'No endpoints match your filters'}
          </p>
        </div>
      ) : (
        <div className={`divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
          {filteredEndpoints.map((endpoint, index) => {
            const selected = isSelected(endpoint);
            const deletable = !!onDeleteUrl;
            const endpointKey = `${endpoint.method}-${endpoint.url}-${index}`;

            return (
              <div
                key={endpointKey}
                className={`group flex items-start gap-2 p-2 transition-colors ${
                  selected
                    ? isDarkMode ? 'bg-blue-900/30' : 'bg-blue-50'
                    : isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-50'
                }`}
              >
                <label className="flex items-start gap-2 flex-1 min-w-0 cursor-pointer">
                  <div className="flex items-center pt-0.5">
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => toggleEndpoint(endpoint)}
                      className="h-3 w-3 text-blue-600 rounded"
                    />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${
                        endpoint.method === 'GET' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                        : endpoint.method === 'POST' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                        : endpoint.method === 'PUT' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300'
                        : endpoint.method === 'DELETE' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                        : endpoint.method === 'PATCH' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                        : isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {endpoint.method}
                      </span>
                      {endpoint.source === 'paste' && (
                        <span className={`text-[8px] px-1 py-0.5 rounded ${isDarkMode ? 'bg-indigo-900/40 text-indigo-300' : 'bg-indigo-100 text-indigo-600'}`}>pasted</span>
                      )}
                      {endpoint.source === 'send_to_authsmash' && (
                        <span className={`text-[8px] px-1 py-0.5 rounded ${isDarkMode ? 'bg-amber-900/40 text-amber-300' : 'bg-amber-100 text-amber-600'}`}>sent</span>
                      )}
                      {selected && <FontAwesomeIcon icon={faCheckSquare} className="text-blue-400 text-xs" />}
                    </div>

                    <div className={`text-[10px] font-mono break-all ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                      {endpoint.url}
                    </div>
                  </div>
                </label>

                {/* Permanent delete (project URLs only) */}
                {deletable && onDeleteUrl && (
                  <button
                    onClick={(e) => handleDelete(endpoint, e)}
                    className={`opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded flex-shrink-0 ${
                      isDarkMode ? 'text-red-400 hover:bg-red-900/30' : 'text-red-500 hover:bg-red-50'
                    }`}
                    title="Remove this URL from the project"
                  >
                    <FontAwesomeIcon icon={faTrash} className="text-[10px]" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  // Small bar above the list: count + expand-to-modal button
  const renderListToolbar = () => (
    <div className="flex items-center justify-between px-0.5">
      <span className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
        {selectionStats.filtered === transformedEndpoints.length
          ? `${transformedEndpoints.length} URL${transformedEndpoints.length !== 1 ? 's' : ''}`
          : `${selectionStats.filtered} of ${transformedEndpoints.length}`}
        {selectionStats.visible > 0 && ` · ${selectionStats.visible} checked`}
      </span>
      <button
        onClick={() => setIsExpanded(true)}
        className={`flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium transition-colors ${
          isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
        }`}
        title="Expand the URL list for a larger overview"
      >
        <FontAwesomeIcon icon={faUpRightAndDownLeftFromCenter} className="text-[9px]" />
        Expand
      </button>
    </div>
  );

  // Paste-URLs toggle + panel (reused inline and in the expanded modal)
  const renderPasteSection = () => (
    <>
      <button
        onClick={() => setShowPaste(v => !v)}
        className={`w-full px-2 py-1.5 rounded text-xs font-medium flex items-center justify-center gap-1.5 border transition-colors ${
          showPaste
            ? 'bg-indigo-600 text-white border-indigo-600'
            : isDarkMode
              ? 'bg-gray-800 text-indigo-300 border-indigo-800 hover:bg-gray-700'
              : 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100'
        }`}
      >
        <FontAwesomeIcon icon={showPaste ? faTimes : faPaste} />
        {showPaste ? 'Close paste panel' : 'Paste URLs'}
      </button>

      {showPaste && (
        <div className={`border rounded p-2 space-y-2 ${isDarkMode ? 'border-indigo-800 bg-gray-900/40' : 'border-indigo-200 bg-indigo-50/40'}`}>
          <div className={`text-[10px] ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            One URL per line. Optionally prefix a method, e.g. <span className="font-mono">POST https://host/api</span>.
            Invalid lines are ignored when adding.
          </div>

          <div className="flex items-center gap-1.5">
            <span className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>Default method</span>
            <select
              value={pasteMethod}
              onChange={(e) => setPasteMethod(e.target.value)}
              className={`px-2 py-1 border rounded text-xs ${
                isDarkMode ? 'bg-gray-900 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700'
              }`}
            >
              {HTTP_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>

          <textarea
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            rows={10}
            spellCheck={false}
            placeholder={'https://portal-dev.example.com/api/users\nhttps://portal-dev.example.com/api/orders\nPOST https://portal-dev.example.com/api/orders'}
            className={`w-full px-2 py-1.5 border rounded text-[11px] font-mono resize-y min-h-[140px] ${
              isDarkMode
                ? 'bg-gray-900 border-gray-600 text-gray-200 placeholder-gray-600'
                : 'bg-white border-gray-300 text-gray-800 placeholder-gray-400'
            }`}
          />

          {/* Live validation feedback */}
          {pasteText.trim().length > 0 && (
            <div className="flex items-center gap-3 text-[11px]">
              <span className="flex items-center gap-1 text-green-500 font-medium">
                <FontAwesomeIcon icon={faCheckCircle} />
                {pasteResult.valid.length} valid
              </span>
              <span className={`flex items-center gap-1 font-medium ${pasteResult.invalidLines.length > 0 ? 'text-red-500' : (isDarkMode ? 'text-gray-500' : 'text-gray-400')}`}>
                <FontAwesomeIcon icon={faTimesCircle} />
                {pasteResult.invalidLines.length} invalid
              </span>
            </div>
          )}

          {/* Invalid line preview */}
          {pasteResult.invalidLines.length > 0 && (
            <div className={`max-h-20 overflow-y-auto rounded border text-[10px] font-mono p-1.5 ${
              isDarkMode ? 'border-red-900/50 bg-red-900/10 text-red-300' : 'border-red-200 bg-red-50 text-red-600'
            }`}>
              {pasteResult.invalidLines.slice(0, 50).map((line, i) => (
                <div key={i} className="truncate">✕ {line}</div>
              ))}
              {pasteResult.invalidLines.length > 50 && (
                <div className="opacity-70">…and {pasteResult.invalidLines.length - 50} more</div>
              )}
            </div>
          )}

          <div className="flex gap-1.5">
            <button
              onClick={handleAddPasted}
              disabled={pasteResult.valid.length === 0}
              className="flex-1 px-2 py-1.5 bg-indigo-600 text-white rounded text-xs font-medium hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center gap-1.5"
            >
              <FontAwesomeIcon icon={faPlus} />
              Add {pasteResult.valid.length} URL{pasteResult.valid.length !== 1 ? 's' : ''}
            </button>
            <button
              onClick={() => setPasteText('')}
              disabled={!pasteText}
              className={`px-2 py-1.5 rounded text-xs font-medium disabled:opacity-40 ${
                isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Clear
            </button>
          </div>
        </div>
      )}
    </>
  );

  return (
    <div className="space-y-2">
      {/* Header with stats */}
      <div className={`border rounded p-2 ${
        isDarkMode ? 'bg-blue-900/20 border-blue-700' : 'bg-blue-50 border-blue-200'
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faCheckSquare} className="text-blue-400" />
            <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Manual URL Selection</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-xs">
              <span className={`font-bold ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>{selectionStats.total}</span>
              <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}> selected</span>
            </div>
            {selectionStats.total > 0 && (
              <button
                onClick={() => onSelectionChange([])}
                className={`px-2 py-0.5 rounded text-[10px] font-medium flex items-center gap-1 transition-colors ${
                  isDarkMode
                    ? 'bg-red-900/30 text-red-400 hover:bg-red-900/50 border border-red-800'
                    : 'bg-red-50 text-red-600 hover:bg-red-100 border border-red-200'
                }`}
                title="Uncheck all endpoints (does not delete them from the project)"
              >
                <FontAwesomeIcon icon={faTimesCircle} className="text-[9px]" />
                Clear Selection
              </button>
            )}
          </div>
        </div>
        <div className={`mt-1 text-[10px] ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          {transformedEndpoints.length} URL{transformedEndpoints.length !== 1 ? 's' : ''} in this project
          {selectionStats.filtered !== transformedEndpoints.length &&
            ` · showing ${selectionStats.filtered}`}
        </div>
      </div>

      {/* Paste URLs */}
      {renderPasteSection()}

      {/* Search and Filter Controls */}
      {renderControls()}

      {/* List toolbar (count + expand) */}
      {renderListToolbar()}

      {/* Endpoint List (compact inline) */}
      {renderList('max-h-64')}

      {/* Footer Info */}
      {filteredEndpoints.length > 0 && (
        <div className={`text-[9px] flex items-start gap-1 ${isDarkMode ? 'text-gray-600' : 'text-gray-500'}`}>
          <FontAwesomeIcon icon={faInfoCircle} className="text-xs mt-0.5" />
          <span>
            Check a row to include it in the test; unchecking only deselects it (the URL stays listed).
            Use the trash icon to permanently remove a project URL.
          </span>
        </div>
      )}

      {/* Expanded (large) view */}
      {isExpanded && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setIsExpanded(false)}>
          <div
            className={`w-full max-w-4xl max-h-[90vh] flex flex-col rounded-lg border shadow-2xl ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className={`flex items-center justify-between px-4 py-3 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon={faCheckSquare} className="text-blue-400" />
                <span className={`text-sm font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                  Manual URL Selection
                </span>
                <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {selectionStats.total} selected · {transformedEndpoints.length} total
                </span>
              </div>
              <button
                onClick={() => setIsExpanded(false)}
                className={`p-1.5 rounded ${isDarkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'}`}
                aria-label="Close"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            {/* Modal body */}
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {renderPasteSection()}
              {renderControls()}
              {renderList('max-h-[55vh]')}
            </div>

            {/* Modal footer */}
            <div className={`flex items-center justify-between px-4 py-3 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Showing {selectionStats.filtered} of {transformedEndpoints.length}
                {selectionStats.visible > 0 && ` · ${selectionStats.visible} checked here`}
              </span>
              <button
                onClick={() => setIsExpanded(false)}
                className="px-5 py-2 bg-indigo-600 text-white rounded text-sm font-medium hover:bg-indigo-700"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuthSmashUrlSelector;
